import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useRouteContext, useRouter, useSearch } from "@tanstack/react-router";
import type { MessageKey } from "@gioia365/i18n";
import { authApi, browserNavigation, type AuthFailure } from "../api/auth";
import { healthQuery, meQuery, myMembershipsQuery, platformOrganizationsQuery } from "../api/queries";
import { PublicShell } from "../design/components/PublicShell";
import { Button } from "../design/components/Button";
import { Card } from "../design/components/Card";
import { TextField } from "../design/components/TextField";
import { useT } from "../i18n/locale";
import { TECHNICAL_PATHS } from "../session/switch-tab";
import { clearTabContext, readTabContext, type TabContext } from "../session/tab-context";
import { describeAuthFailure } from "./auth-failure";
import "./auth.css";

/**
 * Dove tornare dopo l'accesso: soltanto una pagina di Gioia365 (stessa origine), mai un indirizzo esterno
 * né una rotta tecnica, che cambierebbe l'azienda o la Vista Commerciale, o farebbe uscire subito dopo l'accesso.
 */
function safeReturnPath(da: string | undefined): string {
  if (!da || !da.startsWith("/") || da.startsWith("//")) return "/";
  let url: URL;
  try {
    url = new URL(da, window.location.origin);
  } catch {
    return "/";
  }
  if (url.origin !== window.location.origin) return "/";
  if (TECHNICAL_PATHS.has(url.pathname)) return "/";
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Se la sessione appena aperta lavora ancora nell'azienda rimasta nella scheda: l'appartenenza è fra le sue, oppure, per
 * il Super admin di Gioia365, l'azienda c'è ed è attiva. Le letture restano nella cache, dove la guardia le ritrova.
 */
async function tabStillWorks(queryClient: QueryClient, tab: TabContext): Promise<boolean> {
  if (tab.kind === "membership") {
    const memberships = await queryClient.fetchQuery(myMembershipsQuery);
    return memberships.some((membership) => membership.membershipId === tab.membershipId);
  }
  const me = await queryClient.fetchQuery(meQuery);
  if (!me?.isPlatformAdmin) return false;
  const { organizations } = await queryClient.fetchQuery(platformOrganizationsQuery);
  return organizations.some((organization) => organization.id === tab.organizationId && organization.status === "active");
}

/**
 * Dopo un accesso, con la password o con Google (AZI-11). La nuova sessione non eredita nulla dalla cache della scheda: né `["me"]`,
 * che la guardia aveva ricordato come assente, né dati letti prima da un'altra persona; la guardia rilegge la sessione. L'azienda
 * della scheda resta, con la Vista Commerciale e la pagina da cui si veniva, se la sessione nuova ci lavora ancora (la sessione era
 * soltanto scaduta). Se non ci lavora più (una demo riavviata, un'appartenenza chiusa intanto) porterebbe a «non è più
 * disponibile»: si dimentica, e si entra come da una scheda nuova, con un'azienda sola dentro e con più aziende la scelta. L'avviso
 * resta per l'azienda che sparisce durante la sessione. Se la verifica non riesce (la rete) la scheda resta com'è e decide la guardia.
 * Il ritorno va dal browser e non dal router: l'indirizzo porta davanti l'azienda (`/academy/lead`, 4 ottobre 2026) e soltanto la
 * lettura dell'indirizzo del browser la consegna alla guardia, anche in una scheda che non la conosce.
 */
async function enterAfterSignIn(input: { queryClient: QueryClient; tabStorage: Storage; history: { push: (path: string) => void }; da: string | undefined }) {
  input.queryClient.removeQueries();
  const tab = readTabContext(input.tabStorage);
  if (tab) {
    const works = await tabStillWorks(input.queryClient, tab).catch(() => true);
    if (!works) clearTabContext(input.tabStorage);
  }
  input.history.push(safeReturnPath(input.da));
}

/**
 * I due ritorni di «Accedi con Google» (AZI-11), sempre su questa pagina: dopo l'accesso con `accesso=google`, dopo un rifiuto con
 * il motivo che Better Auth aggiunge in `error`. Il percorso da cui si veniva viaggia già convalidato e si riconvalida al ritorno.
 */
function googleReturns(da: string | undefined): { callbackURL: string; errorCallbackURL: string } {
  const page = new URL("/login", window.location.origin);
  const back = safeReturnPath(da);
  if (back !== "/") page.searchParams.set("da", back);
  const entered = new URL(page.href);
  entered.searchParams.set("accesso", "google");
  return { callbackURL: entered.href, errorCallbackURL: page.href };
}

/** Perché il ritorno da Google non ha fatto entrare, nei soli termini che la pagina sa spiegare. */
type GoogleRefusal = "unknown" | "cancelled" | "notLinked" | "unverified" | "workspace" | "failed";

const GOOGLE_REFUSALS: Record<GoogleRefusal, MessageKey> = {
  unknown: "auth.google.unknown",
  cancelled: "auth.google.cancelled",
  notLinked: "auth.google.notLinked",
  unverified: "auth.google.unverified",
  workspace: "auth.google.workspace",
  failed: "auth.google.failed",
};

/**
 * Il motivo del server in parole della pagina: un'email che non è in Gioia365 (`signup_disabled`), l'annullamento su Google
 * (`access_denied`), un account che non si può legare a una persona con l'email confermata (`account_not_linked`, anche quando è
 * l'identità di Gioia365 a non averla confermata, ed `email_invalid`), un'email che Google non dice verificata
 * (`email_not_verified`), un account Google personale (`workspace_required`); ogni altro è un errore.
 */
function googleRefusalOf(error: string): GoogleRefusal {
  if (error === "signup_disabled") return "unknown";
  if (error === "access_denied") return "cancelled";
  if (error === "account_not_linked" || error === "email_invalid") return "notLinked";
  if (error === "email_not_verified") return "unverified";
  if (error === "workspace_required") return "workspace";
  return "failed";
}

/** Il marchio «G» di Google, come nel pulsante standard di Google. */
function GoogleMark() {
  return (
    <svg className="g-auth__google-mark" viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

type ForgotPhase = "editing" | "sending" | "sent";

/**
 * «Password dimenticata?» (piano M1b D10). La risposta è sempre la stessa, che l'email abbia un account o no.
 * Se il server non manda email (`emailDelivery` falso in `GET /api/health`) il pannello non promette nulla e
 * dice a chi chiedere un link nuovo.
 */
function ForgotPasswordPanel({ initialEmail, onBack }: { initialEmail: string; onBack: () => void }) {
  const t = useT();
  const health = useQuery(healthQuery);
  const [email, setEmail] = useState(initialEmail.trim());
  const [phase, setPhase] = useState<ForgotPhase>("editing");
  const [failure, setFailure] = useState<AuthFailure | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const resultRef = useRef<HTMLParagraphElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  // All'apertura il focus va sul titolo del pannello, che così viene letto; poi sull'esito, oppure, dopo un
  // rifiuto, sul pulsante riattivato.
  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  useEffect(() => {
    if (phase === "sent") resultRef.current?.focus();
  }, [phase]);
  useEffect(() => {
    if (failure) submitRef.current?.focus();
  }, [failure]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (phase !== "editing") return;
    setPhase("sending");
    setFailure(null);
    const result = await authApi.requestPasswordReset({ email });
    if (result.ok) {
      setPhase("sent");
      return;
    }
    setPhase("editing");
    setFailure(result.failure);
  }

  let content: ReactNode;
  if (health.isPending) {
    content = (
      <p className="g-auth__muted" role="status">
        {t("common.loading")}
      </p>
    );
  } else if (health.isError) {
    content = (
      <>
        <p className="g-auth__message" data-tone="error" role="alert">
          {t("auth.errors.unavailable")}
        </p>
        <div className="g-auth__actions">
          <Button onClick={() => void health.refetch()}>{t("common.retry")}</Button>
        </div>
      </>
    );
  } else if (!health.data.emailDelivery) {
    content = (
      <p className="g-auth__message" role="status">
        {t("auth.forgot.noEmail")}
      </p>
    );
  } else if (phase === "sent") {
    content = (
      <p ref={resultRef} className="g-auth__message g-auth__result" role="status" tabIndex={-1}>
        {t("auth.forgot.sent")}
      </p>
    );
  } else {
    content = (
      <form className="g-stack" onSubmit={(event) => void handleSubmit(event)} aria-labelledby="forgot-title" aria-busy={phase === "sending"}>
        <p className="g-auth__muted">{t("auth.forgot.intro")}</p>
        <TextField
          id="forgot-email"
          label={t("auth.forgot.email")}
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {failure ? (
          <p className="g-auth__message" data-tone="error" role="alert">
            {describeAuthFailure(t, failure)}
          </p>
        ) : null}
        <Button ref={submitRef} variant="primary" type="submit" disabled={phase === "sending"}>
          {phase === "sending" ? t("auth.forgot.submitting") : t("auth.forgot.submit")}
        </Button>
      </form>
    );
  }

  return (
    <div className="g-stack">
      <h1 id="forgot-title" ref={titleRef} className="g-auth__result" tabIndex={-1}>
        {t("auth.forgot.title")}
      </h1>
      {content}
      <div className="g-auth__actions">
        <Button onClick={onBack}>{t("auth.forgot.back")}</Button>
      </div>
    </div>
  );
}

export function LoginPage() {
  const t = useT();
  const router = useRouter();
  const { da, accesso, error } = useSearch({ from: "/login" });
  const queryClient = useQueryClient();
  const tabStorage = useRouteContext({ from: "__root__", select: (context) => context.tabStorage });
  // «Accedi con Google» (AZI-11) soltanto se il server lo offre: senza la risposta, o con un server vecchio, non c'è.
  const googleAvailable = useQuery(healthQuery).data?.googleSignIn === true;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<AuthFailure | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [googleOpening, setGoogleOpening] = useState(false);
  const [googleFailure, setGoogleFailure] = useState<AuthFailure | null>(null);
  const [googleRefusal, setGoogleRefusal] = useState<GoogleRefusal | null>(error === undefined ? null : googleRefusalOf(error));
  const [googleCompleting, setGoogleCompleting] = useState(accesso === "google");
  const passwordRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const forgotRef = useRef<HTMLButtonElement>(null);
  const googleRef = useRef<HTMLButtonElement>(null);
  const returningFromForgot = useRef(false);

  // Il ritorno da Google dopo l'accesso: con la sessione si entra come dopo la password; senza (il cookie non è arrivato) la pagina
  // lo dice e resta com'è.
  useEffect(() => {
    if (accesso !== "google") return;
    let active = true;
    void (async () => {
      const me = await queryClient.fetchQuery(meQuery).catch(() => null);
      if (!active) return;
      if (me) {
        await enterAfterSignIn({ queryClient, tabStorage, history: router.history, da });
        return;
      }
      setGoogleCompleting(false);
      setGoogleRefusal("failed");
    })();
    return () => {
      active = false;
    };
  }, [accesso, da, queryClient, router, tabStorage]);

  // Un inizio rifiutato riattiva il pulsante, che riprende il focus.
  useEffect(() => {
    if (googleFailure) googleRef.current?.focus();
  }, [googleFailure]);

  async function handleGoogle() {
    if (googleOpening) return;
    setGoogleOpening(true);
    setGoogleFailure(null);
    setGoogleRefusal(null);
    const result = await authApi.startGoogleSignIn(googleReturns(da));
    if (result.ok) {
      browserNavigation.leaveFor(result.url);
      return;
    }
    setGoogleOpening(false);
    setGoogleFailure(result.failure);
  }

  // Chiuso il pannello «Password dimenticata?», il focus torna sul rimando che lo aveva aperto.
  useEffect(() => {
    if (!forgotOpen && returningFromForgot.current) {
      returningFromForgot.current = false;
      forgotRef.current?.focus();
    }
  }, [forgotOpen]);

  // Durante l'invio il pulsante è disabilitato e perde il focus: dopo un errore il focus torna dove si
  // può correggere, cioè sulla password se le credenziali sono sbagliate, altrimenti sul pulsante.
  useEffect(() => {
    if (failure === "invalid_credentials") {
      passwordRef.current?.focus();
      passwordRef.current?.select();
    } else if (failure) {
      submitRef.current?.focus();
    }
  }, [failure]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFailure(null);
    const result = await authApi.signIn({ email, password });
    if (result.ok) {
      await enterAfterSignIn({ queryClient, tabStorage, history: router.history, da });
      return;
    }
    setSubmitting(false);
    setFailure(result.failure);
  }

  if (forgotOpen) {
    return (
      <PublicShell>
        <Card>
          <ForgotPasswordPanel
            initialEmail={email}
            onBack={() => {
              returningFromForgot.current = true;
              setForgotOpen(false);
            }}
          />
        </Card>
      </PublicShell>
    );
  }

  if (googleCompleting) {
    return (
      <PublicShell>
        <Card>
          <div className="g-stack">
            <h1 id="login-title">{t("auth.login.title")}</h1>
            <p className="g-auth__muted" role="status">
              {t("auth.google.completing")}
            </p>
          </div>
        </Card>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <Card>
        <form className="g-stack" onSubmit={(event) => void handleSubmit(event)} aria-labelledby="login-title" aria-busy={submitting}>
          <h1 id="login-title">{t("auth.login.title")}</h1>
          <TextField
            id="email"
            label={t("auth.login.email")}
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            id="password"
            ref={passwordRef}
            label={t("auth.login.password")}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="g-auth__hint">
            <button ref={forgotRef} type="button" className="g-auth__link" onClick={() => setForgotOpen(true)}>
              {t("auth.forgot.link")}
            </button>
          </p>
          {failure ? (
            <p className="g-auth__message" data-tone="error" role="alert">
              {describeAuthFailure(t, failure)}
            </p>
          ) : null}
          <Button ref={submitRef} variant="primary" type="submit" disabled={submitting}>
            {submitting ? t("auth.login.submitting") : t("auth.login.submit")}
          </Button>
        </form>
        {googleAvailable || googleRefusal ? (
          <div className="g-stack g-auth__google">
            {googleAvailable ? (
              <>
                <p className="g-auth__or">{t("auth.google.or")}</p>
                <Button ref={googleRef} disabled={googleOpening} onClick={() => void handleGoogle()}>
                  <GoogleMark />
                  {googleOpening ? t("auth.google.opening") : t("auth.google.button")}
                </Button>
              </>
            ) : null}
            {googleFailure ? (
              <p className="g-auth__message" data-tone="error" role="alert">
                {describeAuthFailure(t, googleFailure)}
              </p>
            ) : googleRefusal === "cancelled" ? (
              <p className="g-auth__message" role="status">
                {t(GOOGLE_REFUSALS.cancelled)}
              </p>
            ) : googleRefusal ? (
              <p className="g-auth__message" data-tone="error" role="alert">
                {t(GOOGLE_REFUSALS[googleRefusal])}
              </p>
            ) : null}
          </div>
        ) : null}
      </Card>
    </PublicShell>
  );
}
